import React, { useCallback, useMemo, useRef, useState } from "react";
import Plotly from "plotly.js-basic-dist";
import Plot from "./PlotlyChart";
import LegendControls from "./LegendControls";
import {
  getPlotlyLegendStyle,
  sortSpeciesForWeightLengthLegend,
} from "../../graphView/colors";
import { getWrYAxisFromValues } from "./axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendTop } from "./plotlyLegendLayout";
import { useGraphV2PlotHeight } from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import {
  wrHundredCategoryFallbackShapes,
  wrHundredLegendTraceCategory,
} from "./plotlyReferenceLineLegend";
import { formatDateTime } from "../../graphView/formatters";
import { WrLabel, LABEL } from "../../../../utils/samplingDisplayLabels";
import "./wrVsLengthPlotly.css";

function seriesKeyToLegendSpecies(key) {
  const m = String(key ?? "").match(/Tag\s+(.+)$/i);
  if (m) return `Tag ${m[1].trim()}`;
  return key;
}

const CARD_W = 280;
const CARD_H = 220;
const formatWrPercent = (value) => `${(Number(value || 0) * 100).toFixed(0)}%`;

function dateToMs(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const d = new Date(value);
  const t = d.getTime();
  return Number.isNaN(t) ? null : t;
}

function formatHoverDate(x) {
  if (x == null || x === "") return "";
  if (typeof x === "string") return x;
  return formatDateTime(x);
}

function pickEvenlySpacedIndices(n, maxTicks) {
  if (n <= 0) return [];
  if (n <= maxTicks) return Array.from({ length: n }, (_, i) => i);
  const idx = new Set([0, n - 1]);
  const inner = maxTicks - idx.size;
  for (let k = 1; k <= inner; k++) {
    idx.add(Math.round((k * (n - 1)) / (inner + 1)));
  }
  return [...idx].sort((a, b) => a - b);
}

/**
 * Relative weight (Wr) vs sampling date for each tagged fish (one line per tag + species).
 * Data rows come from `extractTaggedWrVsDate` with `wr`, `date`, `species`, `tagNumber`, `seriesKey`.
 */
export default function TaggedWrVsDateChart({ graphData, isMobile }) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const processed = useMemo(() => {
    const sorted = [...graphData].sort((a, b) => {
      const ta = dateToMs(a.date);
      const tb = dateToMs(b.date);
      if (ta == null && tb == null) return 0;
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb;
    });
    return sorted.filter((d) => {
      const w = Number(d.wr);
      return d.isTagged && d.tagNumber && Number.isFinite(w) && dateToMs(d.date) != null;
    });
  }, [graphData]);

  const xCategoryAxis = useMemo(() => {
    const msToLabel = new Map();
    processed.forEach((row) => {
      const ms = dateToMs(row.date);
      if (ms == null) return;
      const lab = String(row.date ?? "").trim();
      if (!msToLabel.has(ms)) {
        msToLabel.set(ms, lab || formatDateTime(ms));
      }
    });
    const sortedMs = [...msToLabel.keys()].sort((a, b) => a - b);
    const categoryarray = sortedMs.map((ms) => msToLabel.get(ms));
    const msToCategoryLabel = new Map(sortedMs.map((ms) => [ms, msToLabel.get(ms)]));
    return { categoryarray, msToCategoryLabel };
  }, [processed]);

  const { categoryarray, msToCategoryLabel } = xCategoryAxis;

  const xAxisTickLayout = useMemo(() => {
    const n = categoryarray.length;
    if (n === 0) {
      return {
        tickvals: null,
        ticktext: null,
        tickangle: 0,
        marginBottom: isMobile ? 100 : 90,
        tickFontSize: isMobile ? 14 : 16,
      };
    }

    const labelCap = isMobile ? 10 : 16;
    const ix = pickEvenlySpacedIndices(n, labelCap);
    const displayLabels = ix.map((i) => categoryarray[i]);

    const tickangle = 0;

    const marginBottom = isMobile
      ? Math.min(200, 100 + Math.min(displayLabels.length * 3, 72))
      : Math.min(200, 96 + Math.min(displayLabels.length * 3, 64));

    const tickFontSize =
      n > 14 ? (isMobile ? 9 : 10) : n > 9 ? (isMobile ? 11 : 12) : isMobile ? 14 : 16;

    return {
      tickvals: displayLabels,
      ticktext: displayLabels,
      tickangle,
      marginBottom,
      tickFontSize,
    };
  }, [categoryarray, isMobile]);

  const seriesKeys = useMemo(() => {
    const keys = [...new Set(processed.map((d) => d.seriesKey).filter(Boolean))];
    return keys.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [processed]);

  const orderedLegendSpecies = useMemo(
    () => sortSpeciesForWeightLengthLegend(seriesKeys.map(seriesKeyToLegendSpecies)),
    [seriesKeys]
  );

  const traces = useMemo(() => {
    const catX = (r) => {
      const ms = dateToMs(r.date);
      if (ms == null) return String(r.date ?? "");
      return msToCategoryLabel.get(ms) ?? String(r.date ?? "");
    };

    const t = [];
    seriesKeys.forEach((key) => {
      const rows = processed
        .filter((d) => d.seriesKey === key)
        .sort((a, b) => (dateToMs(a.date) || 0) - (dateToMs(b.date) || 0));
      if (!rows.length) return;
      const sp = rows[0].species || "Unknown";
      const legendSpecies = seriesKeyToLegendSpecies(key);
      const { color, symbol: shape } = getPlotlyLegendStyle(legendSpecies, orderedLegendSpecies);
      const n = rows.length;
      t.push({
        type: "scatter",
        mode: "markers",
        name: `${key} (${n})`,
        x: rows.map(catX),
        y: rows.map((r) => Number(r.wr)),
        marker: {
          color: color,
          size: 9,
          symbol: shape,
          line: { color: "white", width: 1.5 },
        },
        customdata: rows.map((r) => [
          sp,
          String(r.tagNumber || ""),
          color,
          Number(r.wr),
        ]),
        hovertemplate: "<extra></extra>",
      });
    });
    const refTrace = wrHundredLegendTraceCategory(categoryarray);
    if (refTrace) t.push(refTrace);
    return t;
  }, [processed, seriesKeys, msToCategoryLabel, orderedLegendSpecies, categoryarray]);

  const legendLayout = useMemo(
    () =>
      plotlyHorizontalLegendTop(traces.length, isMobile, {
        legendLabels: traces.map((t) => t.name),
        legendEntryWidthPx: isMobile ? 168 : 218,
      }),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 520,
    desktopDefault: 700,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const wrYAxis = useMemo(
    () => getWrYAxisFromValues(processed.map((d) => Number(d.wr || 0))),
    [processed]
  );

  const tickVar = "dateTaggedWr";

  const onHover = useCallback((eventData) => {
    const pt = eventData?.points?.[0];
    const el = wrapRef.current;
    const evt = eventData?.event;
    if (!pt || el == null || typeof pt.y !== "number" || !Number.isFinite(pt.y)) {
      return;
    }
    const cd = pt.customdata;
    if (!Array.isArray(cd) || cd.length < 4) {
      return;
    }

    let cx;
    let cy;
    if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
      cx = evt.clientX;
      cy = evt.clientY;
    } else if (pt.bbox) {
      cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
      cy = pt.bbox.y0;
    } else {
      return;
    }

    const speciesName = String(cd[0] ?? "");
    const tagNum = String(cd[1] ?? "");
    const seriesColor = String(cd[2] ?? "#059669");
    const wrVal = Number(cd[3]);
    const dateStr = formatHoverDate(pt.x);
    const aboveStandard = wrVal >= 1;

    const rect = el.getBoundingClientRect();
    const pad = 12;
    let left = cx - rect.left + pad;
    let top = cy - rect.top + pad;
    if (left + CARD_W > rect.width - 8) {
      left = cx - rect.left - CARD_W - pad;
    }
    if (top + CARD_H > rect.height - 8) {
      top = cy - rect.top - CARD_H - pad;
    }
    left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
    top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

    setTip({
      left,
      top,
      species: speciesName,
      tagNumber: tagNum,
      seriesColor,
      dateStr,
      wr: wrVal,
      aboveStandard,
    });
  }, []);

  const onUnhover = useCallback(() => setTip(null), []);

  const handleSelectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = { visible: traces.map(() => true) };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  const handleDeselectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = { visible: traces.map(() => "legendonly") };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  return (
    <div
      ref={wrapRef}
      className="avg-wr-vs-date-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <LegendControls
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: xAxisTickLayout.marginBottom, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          shapes: wrHundredCategoryFallbackShapes(categoryarray),
          xaxis: {
            type: "category",
            categoryorder: "array",
            categoryarray,
            title: {
              ...plotlyAxisTitle("Date", isMobile),
              font: { ...plotlyAxisTitle("Date", isMobile).font, size: isMobile ? 16 : 22 },
            },
            ...(xAxisTickLayout.tickvals && xAxisTickLayout.tickvals.length
              ? {
                  tickmode: "array",
                  tickvals: xAxisTickLayout.tickvals,
                  ticktext: xAxisTickLayout.ticktext,
                }
              : {}),
            tickangle: xAxisTickLayout.tickangle,
            automargin: true,
            ...plotlyAxisBase(isMobile, tickVar),
            tickfont: {
              ...plotlyAxisBase(isMobile, tickVar).tickfont,
              size: xAxisTickLayout.tickFontSize,
            },
          },
          yaxis: {
            title: {
              ...plotlyAxisTitle(LABEL.WR_PCT_HTML, isMobile),
              font: {
                ...plotlyAxisTitle(LABEL.WR_PCT_HTML, isMobile).font,
                size: isMobile ? 16 : 22,
              },
            },
            tickmode: "array",
            tickvals: wrYAxis.yTicks,
            ticktext: wrYAxis.yTicks.map((tick) => formatWrPercent(tick)),
            range: [wrYAxis.yMin, wrYAxis.yMax],
            ...plotlyAxisBase(isMobile, tickVar),
            tickfont: { ...plotlyAxisBase(isMobile, tickVar).tickfont, size: isMobile ? 14 : 16 },
          },
          legend: legendLayout.legend,
          hovermode: "closest",
        }}
        config={{ responsive: true, displaylogo: false }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--avg-wr${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title wr-length-hover-card__label-with-swatch">
              <span
                className="wr-length-hover-card__dot-swatch"
                style={{ backgroundColor: tip.seriesColor }}
                aria-hidden
              />
              {tip.species}
              {tip.tagNumber ? ` · Tag ${tip.tagNumber}` : ""}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Date:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--date">
                {tip.dateStr}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">
                <WrLabel />:
              </span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--wr">
                {formatWrPercent(tip.wr)}
              </span>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard ? (
              <>
                ✓ At or above <WrLabel /> = 100%
              </>
            ) : (
              <>
                Below <WrLabel /> = 100%
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
